import { THIS_WEEKEND } from '@eventbrite/discover-utils';
import { gettext } from '@eventbrite/i18n';
import { logEvent } from '@eventbrite/statsig';
import loadable from '@loadable/component';
import React from 'react';
import { StickyContainer } from 'react-sticky';
import { SeasonalRibbon } from '../../../components/SeasonalRibbon';
import { LazyRenderer } from '../components/LazyRenderer';
import { LighthouseCreator } from '../components/LighthouseCreator';
import { NightlifeEvents } from '../components/NightlifeEvents';
import { STATSIG_CATEGORYBROWSE_PAGE_VIEW } from '../constants/analytics';
import { FREE, MOST_POPULAR } from '../constants/bucketKey';
import { CategoryBrowseBreadCrumb } from '../containers/CategoryBrowseBreadCrumb';
import { CategoryBrowseDateFilter } from '../containers/CategoryBrowseDateFilter';
import { CategoryBrowseDynamicFormatEvents } from '../containers/CategoryBrowseDynamicFormatEvents';
import { CategoryBrowseEvents } from '../containers/CategoryBrowseEvents';
import { CategoryBrowseLocationAutocomplete } from '../containers/CategoryBrowseLocationAutocomplete';
import { CategoryBrowseRelatedCategories } from '../containers/CategoryBrowseRelatedCategories';
import { TrendingTagEvents } from '../containers/TrendingTagEvents';
import { useEnvContext, useRouteContext } from '../context';
import { useLocation } from '../hooks/queries/location';
import { CATEGORY_BROWSE_PLACEMENT } from '../utils/analytics';
import { getLanguageSubdirectorySubPath } from '../utils/localeSubPath';
import './CategoryBrowsePage.scss';
import { categoryBrowseBucketsNewAdsExperiment } from './Experiments';

const CategoryBrowseOrganizers = loadable(
    () => import('../containers/CategoryBrowseOrganizers'),
);

const CategoryBrowseNearbyCities = loadable(
    () => import('../containers/CategoryBrowseNearbyCities'),
);

const CategoryBrowsePopularCategories = loadable(
    () => import('../containers/CategoryBrowsePopularCategories'),
    {
        resolveComponent: (components: {
            CategoryBrowsePopularCategories: React.FunctionComponent;
        }) => components.CategoryBrowsePopularCategories,
    },
);

const CategoryBrowsePopularFormats = loadable(
    () => import('../containers/CategoryBrowsePopularFormats'),
);

const CategoryBrowsePopularTimes = loadable(
    () => import('../containers/CategoryBrowsePopularTimes'),
);
const CategoryBrowseHeaderContainer = loadable(
    () => import('../containers/CategoryBrowseHeaderContainer'),
    {
        resolveComponent: (components: {
            CategoryBrowseHeaderContainer: React.ReactElement;
        }) => components.CategoryBrowseHeaderContainer,
    },
);

//TODO: Set date filter to leverage current future and date when both applied
export const CategoryBrowsePage: React.FunctionComponent = () => {
    // Env and General Data
    const envContext: any = useEnvContext();
    const user = envContext?.user;
    const locale = envContext?.env?.localeInfo?.locale || 'en_US';
    const isBotRequest = envContext?.isBotRequest;

    //Current Tag and Search Values
    const { parsedValues, location, featureFlags } = useRouteContext();
    const activeTag = parsedValues?.activeTag || '';
    const date = parsedValues?.date;
    const customDate = parsedValues?.customDate;

    React.useEffect(() => {
        if (activeTag) {
            logEvent(STATSIG_CATEGORYBROWSE_PAGE_VIEW);
        }
    }, [activeTag]);

    const { data: locationData } = useLocation();
    const placeId = locationData?.placeId || '';

    const adSlotsExp =
        categoryBrowseBucketsNewAdsExperiment() === 'ad1' ? [1] : [];

    return (
        <StickyContainer style={{ overflowY: 'auto' }}>
            {location?.pathname.split('/')[3] != 'holiday' && (
                <SeasonalRibbon
                    season="holidays"
                    locationSlug={
                        envContext?.request?.default_location_for_tld
                            ?.location_slug || 'online'
                    }
                    country={locationData?.country}
                    browseSurface={'category-browse'}
                    isMobile={envContext?.isMobile}
                />
            )}
            <CategoryBrowseBreadCrumb
                languageSubdirectory={getLanguageSubdirectorySubPath(
                    envContext,
                )}
            />
            <CategoryBrowseHeaderContainer
                activeCategory={activeTag}
                isAuthenticated={!!user?.isAuthenticated}
            />
            <main className="category-browse__main">
                <LazyRenderer numChildInitialRender={isBotRequest ? -1 : 2}>
                    <section className="category-browse__filters">
                        <CategoryBrowseLocationAutocomplete />
                        <CategoryBrowseDateFilter locale={locale} />
                    </section>
                    <CategoryBrowseRelatedCategories />
                    <CategoryBrowseEvents
                        bucketName={CATEGORY_BROWSE_PLACEMENT}
                        bucketKey={MOST_POPULAR}
                        bucketPosition="2"
                        adSlots={[1, 4]}
                        renderEmptyState
                    />
                    <LighthouseCreator adSlots={adSlotsExp} />
                    {date !== 'current_future' || !!customDate ? null : (
                        <CategoryBrowseEvents
                            queryOverload={{
                                page_size: 8,
                                dates: ['this_weekend'],
                                aggs: {},
                            }}
                            title={gettext('This weekend')}
                            bucketName={CATEGORY_BROWSE_PLACEMENT}
                            bucketKey={THIS_WEEKEND}
                            bucketPosition="3"
                            lazyLoadCarouselElements
                            adSlots={[1]}
                        />
                    )}
                    <NightlifeEvents />
                    <CategoryBrowseOrganizers user={user} location={location} />
                    <CategoryBrowseDynamicFormatEvents
                        bucketName={CATEGORY_BROWSE_PLACEMENT}
                        bucketNumber={0}
                        bucketPosition="5"
                        adSlots={adSlotsExp}
                    />
                    <CategoryBrowseEvents
                        queryOverload={{
                            page_size: 8,
                            price: 'free',
                            aggs: {},
                        }}
                        title={gettext('Free')}
                        bucketName={CATEGORY_BROWSE_PLACEMENT}
                        bucketKey={FREE}
                        bucketPosition="6"
                        lazyLoadCarouselElements
                        forceRenderHeader={isBotRequest}
                        adSlots={[1]}
                    />
                    <TrendingTagEvents
                        title={gettext('Trending Tags')}
                        bucketName={CATEGORY_BROWSE_PLACEMENT}
                        bucketKey="trending_tag_events"
                        bucketPosition="7"
                        placeId={placeId}
                        lazyLoadCarouselElements
                    />
                    <CategoryBrowseDynamicFormatEvents
                        bucketName={CATEGORY_BROWSE_PLACEMENT}
                        bucketNumber={1}
                        bucketPosition="8"
                        adSlots={adSlotsExp}
                    />
                    <CategoryBrowseDynamicFormatEvents
                        bucketName={CATEGORY_BROWSE_PLACEMENT}
                        bucketNumber={2}
                        bucketPosition="9"
                        adSlots={adSlotsExp}
                    />
                    <CategoryBrowsePopularFormats />
                    <CategoryBrowsePopularTimes />
                    <CategoryBrowsePopularCategories
                        enableSeasonalModule={
                            featureFlags?.enableSeasonalModule
                        }
                    />
                    <CategoryBrowseNearbyCities />
                </LazyRenderer>
            </main>
        </StickyContainer>
    );
};

export default CategoryBrowsePage;
